import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { Add, MyLocation, Remove } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Circle, CircleMarker, MapContainer, TileLayer } from 'react-leaflet';
import { GeoCoords, GeoMeasurement } from './geoApi';
import { MAX_PIN_ZOOM } from './geoMath';
import { BORDER, PRIMARY, TEXT } from './geoStyles';

/**
 * Карта с пином по центру, как при выборе адреса в такси: пин стоит на месте, двигается карта.
 * Масштабирование колесом, двойным нажатием и щипком идет вокруг центра, поэтому точка под пином не уезжает.
 * Наружу отдается только центр и масштаб после остановки карты.
 */
export interface MapView extends GeoCoords {
  zoom: number;
}

/** Команда перелета (результат поиска): seq отличает повторный перелет в ту же точку */
export interface MapCommand extends MapView {
  seq: number;
}

type MapHeight = number | string | { xs?: number | string; sm?: number | string; md?: number | string };

interface PinMapProps {
  start: MapView;
  command: MapCommand | null;
  measured: GeoMeasurement | null;
  /** Подпись над пином: адрес под ним или подсказка; пусто - без подписи */
  label: string;
  height: MapHeight;
  onMove: (view: MapView) => void;
}

const LAYERS = {
  scheme: {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>'
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community'
  }
};
type LayerKey = keyof typeof LAYERS;

// Плитки обоих слоев есть до 19-го масштаба, ближе карта растягивает их: пин можно ставить точнее, чем видно здание
const NATIVE_TILE_ZOOM = 19;
const MEASURED_ZOOM = 18;

const controlSx = { bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '8px', boxShadow: '0 1px 4px rgba(15,23,42,0.12)', pointerEvents: 'auto' } as const;

const PinMap: React.FC<PinMapProps> = ({ start, command, measured, label, height, onMove }) => {
  const [map, setMap] = useState<L.Map | null>(null);
  const [moving, setMoving] = useState(false);
  const [layer, setLayer] = useState<LayerKey>('scheme');
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!map) return undefined;
    const started = () => setMoving(true);
    const ended = () => {
      setMoving(false);
      const center = map.getCenter().wrap();
      onMoveRef.current({ latitude: center.lat, longitude: center.lng, zoom: map.getZoom() });
    };
    map.on('movestart', started);
    map.on('moveend', ended);
    ended();
    // Карта в диалоге или на телефоне меняет размер после показа (анимация, адресная строка браузера)
    const timer = window.setTimeout(() => map.invalidateSize(), 200);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => map.invalidateSize()) : null;
    if (observer && boxRef.current) observer.observe(boxRef.current);
    return () => {
      map.off('movestart', started);
      map.off('moveend', ended);
      window.clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, [map]);

  useEffect(() => {
    if (map && command) map.flyTo([command.latitude, command.longitude], command.zoom, { duration: 0.8 });
  }, [map, command]);

  const toMeasured = () => {
    if (map && measured) map.flyTo([measured.latitude, measured.longitude], Math.max(map.getZoom(), MEASURED_ZOOM), { duration: 0.8 });
  };

  const tiles = LAYERS[layer];

  return (
    <Box
      ref={boxRef}
      sx={{
        position: 'relative', height, borderRadius: '12px', overflow: 'hidden', border: `1px solid ${BORDER}`, bgcolor: '#f1f5f9',
        '& .leaflet-container': { width: '100%', height: '100%', fontFamily: 'inherit', bgcolor: '#f1f5f9' },
        '& .leaflet-control-attribution': { fontSize: '9px', bgcolor: 'rgba(255,255,255,0.8)', px: 0.75 }
      }}
    >
      <MapContainer
        ref={setMap}
        center={[start.latitude, start.longitude]}
        zoom={start.zoom}
        maxZoom={MAX_PIN_ZOOM}
        zoomControl={false}
        scrollWheelZoom="center"
        doubleClickZoom="center"
        touchZoom="center"
      >
        <TileLayer key={layer} url={tiles.url} attribution={tiles.attribution} maxNativeZoom={NATIVE_TILE_ZOOM} maxZoom={MAX_PIN_ZOOM} />
        {measured && (
          <>
            <Circle center={[measured.latitude, measured.longitude]} radius={measured.accuracy} pathOptions={{ color: PRIMARY, weight: 1, fillColor: PRIMARY, fillOpacity: 0.08 }} />
            <CircleMarker center={[measured.latitude, measured.longitude]} radius={6} pathOptions={{ color: '#fff', weight: 2, fillColor: PRIMARY, fillOpacity: 1 }} />
          </>
        )}
      </MapContainer>

      {/* Пин и подпись поверх карты: не ловят нажатия, чтобы карту можно было тащить прямо за пин */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 500, pointerEvents: 'none' }}>
        <Box sx={{
          position: 'absolute', left: '50%', top: '50%', width: 14, height: 6, borderRadius: '50%', bgcolor: 'rgba(15,23,42,0.35)',
          transform: `translate(-50%, -50%) scale(${moving ? 0.6 : 1})`, transition: 'transform 0.15s ease'
        }} />
        <Box sx={{
          position: 'absolute', left: '50%', top: '50%', width: 36, height: 48, lineHeight: 0,
          transform: `translate(-50%, ${moving ? 'calc(-100% - 12px)' : '-100%'})`, transition: 'transform 0.15s ease',
          filter: 'drop-shadow(0 3px 4px rgba(15,23,42,0.3))'
        }}>
          <svg width="36" height="48" viewBox="0 0 36 48" aria-hidden="true">
            <path d="M18 0C8.06 0 0 8.06 0 18c0 12.6 18 30 18 30s18-17.4 18-30C36 8.06 27.94 0 18 0z" fill={PRIMARY} />
            <circle cx="18" cy="18" r="7" fill="#fff" />
          </svg>
        </Box>
        {label && !moving && (
          <Box sx={{
            position: 'absolute', left: '50%', top: 'calc(50% - 56px)', transform: 'translate(-50%, -100%)', maxWidth: 'min(260px, 80%)',
            px: 1.25, py: 0.6, borderRadius: '8px', bgcolor: '#fff', border: `1px solid ${BORDER}`, boxShadow: '0 2px 8px rgba(15,23,42,0.15)',
            color: TEXT, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          }}>
            {label}
          </Box>
        )}

        <ToggleButtonGroup
          exclusive
          size="small"
          value={layer}
          onChange={(_, value: LayerKey | null) => { if (value) setLayer(value); }}
          sx={{ ...controlSx, position: 'absolute', top: 10, left: 10, '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12, fontWeight: 600, px: 1.25, py: 0.4, border: 'none' } }}
        >
          <ToggleButton value="scheme">Схема</ToggleButton>
          <ToggleButton value="satellite">Спутник</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ position: 'absolute', right: 10, top: 10, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <IconButton size="small" aria-label="Приблизить" onClick={() => map && map.zoomIn()} sx={controlSx}><Add fontSize="small" /></IconButton>
          <IconButton size="small" aria-label="Отдалить" onClick={() => map && map.zoomOut()} sx={controlSx}><Remove fontSize="small" /></IconButton>
          {measured && (
            <Tooltip title="Вернуть пин к замеру устройства" placement="left">
              <IconButton size="small" aria-label="К замеру" onClick={toMeasured} sx={{ ...controlSx, color: PRIMARY }}><MyLocation fontSize="small" /></IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PinMap;
